import React from "react";
import CompanySectionCard from "@/screen/Companies/components/CompanySectionCard";
import CountryPrefixes from "@/utils/CountryPrefixes";
import AddressTypes from "@/utils/AddressTypes";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";
import { BlockedNotice, Field, FieldGrid, Input, Select, SubsectionTitle } from "@/screen/Companies/Styles/CompanyFormFieldStyles";
import { FatcaStatusOptions, isFatcaBlocked } from "@/screen/Companies/CompanyRepresentative/companyRepresentativeForm";
import type { CatalogOption, RepresentativeFormData } from "@/screen/Companies/CompanyRepresentative/companyRepresentativeForm";

interface RepresentativeAdditionalDataSectionProps
{
    formData: RepresentativeFormData;
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
}

// TODO: sustituir por el catálogo de países de FinametrixService.getCountries cuando se conecte el backend
const CountryOptions: CatalogOption[] = CountryPrefixes
    .filter((country) => country.iso2)
    .map((country) => ({ value: country.iso2, text: country.name }));

const PhonePrefixOptions: CatalogOption[] = CountryPrefixes
    .filter((country) => country.phone_code)
    .map((country) => ({ value: country.phone_code, text: `${country.name} (${country.phone_code})` }));

const RoadTypeOptions: CatalogOption[] = AddressTypes.map((roadType) => ({ value: roadType.value, text: roadType.text }));

const renderOptions = (options: CatalogOption[]): React.ReactNode =>
(
    <React.Fragment>
        <option value="">-- Seleccionar --</option>
        {/* Hay prefijos compartidos por varios países (p. ej. +1): la clave incluye el índice */}
        {options.map((option, index) => <option key={`${option.value}-${index}`} value={option.value}>{option.text}</option>)}
    </React.Fragment>
);

// Tarjeta 3: datos que no vienen en el DNI y que hacen falta para dar de alta al representante
const RepresentativeAdditionalDataSection: React.FC<RepresentativeAdditionalDataSectionProps> = ({ formData, onChange }) =>
{
    return(
        <CompanySectionCard number={3} title={CompanyOnboardingStrings.additionalDataTitle} description={CompanyOnboardingStrings.additionalDataDescription}>
            <SubsectionTitle>Nacionalidad y contacto</SubsectionTitle>
            <FieldGrid>
                <Field>
                    <span>Nacionalidad <b>*</b></span>
                    <Select name="nationalCountry" value={formData.nationalCountry} onChange={onChange}>{renderOptions(CountryOptions)}</Select>
                </Field>
                <Field>
                    <span>Correo electrónico <b>*</b></span>
                    <Input type="email" name="email" value={formData.email} onChange={onChange} maxLength={255} placeholder="nombre@empresa.com" autoComplete="off" />
                </Field>
                <Field>
                    <span>Prefijo <b>*</b></span>
                    <Select name="countryPrefix" value={formData.countryPrefix} onChange={onChange}>{renderOptions(PhonePrefixOptions)}</Select>
                </Field>
                <Field>
                    <span>Teléfono móvil <b>*</b></span>
                    <Input type="tel" name="phone" value={formData.phone} onChange={onChange} maxLength={12} placeholder="600 000 000" autoComplete="off" />
                </Field>
            </FieldGrid>

            <SubsectionTitle>Dirección postal</SubsectionTitle>
            <FieldGrid>
                <Field>
                    <span>País <b>*</b></span>
                    <Select name="addressCountry" value={formData.addressCountry} onChange={onChange}>{renderOptions(CountryOptions)}</Select>
                </Field>
                <Field>
                    <span>Código postal</span>
                    <Input type="text" name="zipCode" value={formData.zipCode} onChange={onChange} maxLength={10} placeholder="28001" autoComplete="off" />
                </Field>
                <Field>
                    <span>Localidad <b>*</b></span>
                    <Input type="text" name="city" value={formData.city} onChange={onChange} maxLength={100} autoComplete="off" />
                </Field>
                <Field>
                    <span>Provincia</span>
                    <Input type="text" name="state" value={formData.state} onChange={onChange} maxLength={100} autoComplete="off" />
                </Field>
                <Field>
                    <span>Tipo de vía</span>
                    <Select name="roadType" value={formData.roadType} onChange={onChange}>{renderOptions(RoadTypeOptions)}</Select>
                </Field>
                <Field>
                    <span>Dirección <b>*</b></span>
                    <Input type="text" name="streetName" value={formData.streetName} onChange={onChange} maxLength={150} autoComplete="off" />
                </Field>
                <Field>
                    <span>Número</span>
                    <Input type="text" name="streetNumber" value={formData.streetNumber} onChange={onChange} maxLength={10} autoComplete="off" />
                </Field>
                <Field>
                    <span>Piso</span>
                    <Input type="text" name="streetFloor" value={formData.streetFloor} onChange={onChange} maxLength={10} autoComplete="off" />
                </Field>
                <Field>
                    <span>Puerta</span>
                    <Input type="text" name="streetDoor" value={formData.streetDoor} onChange={onChange} maxLength={10} autoComplete="off" />
                </Field>
                <Field>
                    <span>Información adicional</span>
                    <Input type="text" name="streetOther" value={formData.streetOther} onChange={onChange} maxLength={100} placeholder="Escalera, bloque…" autoComplete="off" />
                </Field>
            </FieldGrid>

            <SubsectionTitle>FATCA</SubsectionTitle>
            <FieldGrid>
                <Field>
                    <span>FATCA <b>*</b></span>
                    <Select name="fatcaStatus" value={formData.fatcaStatus} onChange={onChange}>{renderOptions(FatcaStatusOptions)}</Select>
                </Field>
            </FieldGrid>
            {isFatcaBlocked(formData) && <BlockedNotice role="alert">{CompanyOnboardingStrings.fatcaBlocked}</BlockedNotice>}
        </CompanySectionCard>
    );
};

export default RepresentativeAdditionalDataSection;
